/**
 * Edit Company Handler
 * Edit handler for updating company information
 */

import type { ToolExecutionContext } from '../../types/index.js';
import type { EditPreview } from '../../types/index.js';
import { EditHandlerBase } from '../base/index.js';
import { uuidField, objectField } from '../../utils/schema-builder.js';
import { generateDiff, hasChanges } from '../../utils/diff-generator.js';
import { generateConfirmationToken } from '../../utils/confirmation-token.js';
import { createUserGraphQLClient } from '../../utils/graphql-client.js';

/**
 * Edit company handler
 * Fetches current company via GraphQL and generates preview with RLS enforcement
 */
export class EditCompanyHandler extends EditHandlerBase<any> {
  id = 'edit_company';

  schema = {
    type: 'object',
    properties: {
      id: uuidField('UUID of the company to edit'),
      updates: objectField('Fields to update (name, industry, etc.)'),
    },
    required: ['id', 'updates'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<EditPreview> {
    const { id, updates } = params;

    if (!id) {
      throw new Error('Company ID is required');
    }

    if (!updates || Object.keys(updates).length === 0) {
      throw new Error('No updates provided');
    }

    if (!context?.userToken) {
      throw new Error('User token is required for company edit');
    }

    try {
      // Dynamically import GraphQL types and documents
      const { GetCompanyDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      // Fetch current company
      const result = await client.query({
        query: GetCompanyDocument,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const currentCompany = data?.companiesCollection?.edges?.[0]?.node;
      
      if (!currentCompany) {
        throw new Error(`Company with ID "${id}" not found or you don't have permission to edit it`);
      }

      // Generate diff: compare current to merged (patch semantics — only changed fields)
      const merged = { ...currentCompany, ...updates };
      const changes = generateDiff(currentCompany, merged);

      if (!hasChanges(changes)) {
        throw new Error('No changes detected');
      }

      // Generate confirmation token
      const token = generateConfirmationToken({
        entityType: 'company',
        entityId: id,
        changes: updates,
        userId: context?.userId || 'system',
        chatId: context?.chatId || 'system',
        messageId: context?.messageId || 'system',
      });

      return {
        type: 'preview',
        entity: 'company',
        id,
        changes,
        confirmationToken: token,
        preview: merged,
      };
    } catch (error) {
      throw new Error(`Company edit failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
